import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CHART_TYPE, ChartDataSchema, ChartTypes, CommonChartModel, DataConfig, DrillDownConfig } from '../../model/chart.model';
import { DynamicFormArray, FieldSelectedValue, FormFieldConfig, INFO_TYPES, InfoFieldConfig, Props } from '../../model/form.model';
import { API_STATUS, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { DYNAMIC_IDENTIFIER, SPINNER_AREA, SpinnerArea } from '../../model/common.model';
import { CommonModule } from '@angular/common';
import Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import 'highcharts/modules/exporting';
import 'highcharts/modules/drilldown';
import { FieldNames, FieldTypesForOptions } from '../../model/form-fields.model';
import { exportConfig, TitleSchema } from '../../model/dynamic-data-table.model';
import { ExcelExportService } from '../../services/excel-export.service';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { FormContainerComponent } from '../form/form-container/form-container.component';
import { CommonService } from '../../services/common.service';
import { CamelCasePipe } from '../../utils/camel-case.pipe';
import { LoadingSkeletonComponent } from "../loading-skeleton/loading-skeleton.component";

// Register bubble series with Highcharts
(Highcharts as any).seriesTypes.bubble = (Highcharts as any).seriesTypes.scatter;


@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [CommonModule, HighchartsChartModule, DownloadButtonComponent, FormContainerComponent, CamelCasePipe, LoadingSkeletonComponent],
  providers: [CamelCasePipe],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent implements OnInit, OnDestroy {
  @Input() chartSchema!: CommonChartModel;
  @Input() chartType!: ChartTypes;
  @Input() infoFields!: Array<InfoFieldConfig>;
  @Input() elementId = '';
  @Input() containerClass?: string;
  @Input() apiConfig!: SearchConfig;
  @Input() props!: Props;
  @Input() exportConfig: exportConfig | null = null;
  @Input() filterSchema!: DynamicFormArray;
  @Input() globalFilterFields!: Array<any>;
  @Input() requiredGlobalPayloadFields: string[] = [];
  @Input() onLoadFetch = true;
  @Input() defaultLoader = false;
  @Input() set loading(value: boolean) {
    this.isLoading = value;
  }
  @Input() scrollablePlotAreaMinWidth = 0;
  @Input() scrollablePlotAreaRecordThreshold = 0;
  @Input() dataTransform?: (response: any) => any[];
  @Input() set dataInput(value: any) {
    this.updateChartAfterFetch(value);
  }

  public chartRef!: any;
  public beginRender = false;
  public chartOptions: any;
  public dataSource: Array<any> = [];
  public formattedResult: any = []
  Highcharts: typeof Highcharts = Highcharts;
  public spinnerName: SpinnerArea = SPINNER_AREA['Chart'];
  private readonly commonUtils = inject(CommonUtils);
  private readonly exportService = inject(ExcelExportService);
  private readonly commonService = inject(CommonService)
  public globalpayload: any;
  private globalpayloadSubject!: any;
  public globalTitleFields: any = {}
  public updateFlag = false;
  readonly errorResponse: typeof ERROR_RESPONSE = ERROR_RESPONSE;
  public apiErrorOccurred = false;
  isFieldSelected = false;
  private initialFetchRetryTimer: ReturnType<typeof setTimeout> | null = null;
  readonly camelCasePipe = inject(CamelCasePipe)
  private drillAxisTitleStack: Array<{ xTitle: string; yTitle: string }> = [];
  public titleConfig = {
    title: '',
    subTitle: ''
  }
  isLoading = false;
  private latestFetchRequestId = 0;
  ngOnInit() {
    try {
      this.spinnerName = this.spinnerName + this.elementId;
      this.initializeChart();
      if (this.onLoadFetch) {
        this.isLoading = true;
        this.tryInitialFetch();
      }
      this.beginRender = true;
    } catch (e) {
      console.info(e)  
      this.beginRender = true;

    }
  }
  ngOnDestroy() {
    if (this.initialFetchRetryTimer) {
      clearTimeout(this.initialFetchRetryTimer);
      this.initialFetchRetryTimer = null;
    }
    this.globalpayloadSubject?.unsubscribe();
  }

  private syncSelectedFromDefault(field: FormFieldConfig): void {
    if ((field?.selectedValue === '' || field?.selectedValue === null || field?.selectedValue === undefined) &&
      field?.defaultValue !== '' && field?.defaultValue !== null && field?.defaultValue !== undefined) {
      field.selectedValue = field.defaultValue;
    }
  }

  private areOptionFiltersReady(): boolean {
    if (!this.filterSchema?.FieldsArray?.length) {
      return true;
    }

    const fields = this.filterSchema.FieldsArray;
    const requiredBodyKeys = new Set(Object.keys(this.apiConfig?.body ?? {}));
    const relevantOptionFields = fields.filter(field =>
      FieldTypesForOptions.includes(field.fieldType) &&
      (requiredBodyKeys.size === 0 || requiredBodyKeys.has(field.fieldName))
    );

    if (!relevantOptionFields.length) {
      return true;
    }

    return relevantOptionFields.every(field => {
      this.syncSelectedFromDefault(field);
      if (field.isLoading) {
        return false;
      }

      return field.selectedValue !== '' && field.selectedValue !== null && field.selectedValue !== undefined;
    });
  }

  private tryInitialFetch(retryCount = 0): void {
    if (this.areOptionFiltersReady()) {
      this.updatePayloadAndFetch();
      return;
    }

    if (retryCount >= 150) {
      this.isLoading = false;
      return;
    }

    this.initialFetchRetryTimer = setTimeout(() => {
      this.tryInitialFetch(retryCount + 1);
    }, 100);
  }
  chartOnRender: Highcharts.ChartCallbackFunction = chart => {
    this.chartRef = chart;
    this.chartSchema['chartRef'] = chart;
    this.updateTitle(false);
  };
  initializeChart() {
    this.drillAxisTitleStack = [];
    this.updateTitle(false);
    this.chartOptions = this.formChartSchema(this.chartSchema);
    this.updateFilterFields();
  }
  private getCurrentAxisTitles(target: any): { xTitle: string; yTitle: string } {
    const xTitle = target?.xAxis?.[0]?.axisTitle?.textStr ?? target?.xAxis?.[0]?.userOptions?.title?.text ?? '';
    const yTitle = target?.yAxis?.[0]?.axisTitle?.textStr ?? target?.yAxis?.[0]?.userOptions?.title?.text ?? '';
    return { xTitle, yTitle };
  }
  updateTitle(isDrillDown: boolean, field?: FormFieldConfig) {
    const titleConfig: any = {
      titleSchema: null,
      filterFields: this.filterSchema?.FieldsArray ?? [],
      globalTitleFields: this.globalTitleFields ?? {}
    }
    if (isDrillDown && this.chartSchema?.dataSchema?.drillDownConfig?.titleSchema) {
      titleConfig['titleSchema'] = this.chartSchema.dataSchema.drillDownConfig.titleSchema
    } else {
      if (field) {
        const schema = (this.chartSchema['titleSchema'] as Record<string, TitleSchema>)
        if (!schema[field['fieldName']]) {
          return {
            title: '',
            subTitle: ''
          };
        }
      }
      titleConfig['titleSchema'] = this.chartSchema['titleSchema']
    }
    if (!titleConfig.titleSchema) {
      const emptyTitle = { title: '', subTitle: '' };
      this.chartSchema['title'] = emptyTitle.title;
      this.chartSchema['subtitle'] = emptyTitle.subTitle;
      return emptyTitle;
    }

    const titleData = this.commonUtils.updateTitle(titleConfig, field);
    if (!titleData) {
      return { title: '', subTitle: '' };
    }
    this.chartSchema['title'] = titleData['title'];
    this.chartSchema['subtitle'] = titleData['subTitle']
    if (this.chartRef && titleData['title']) {
      try {
        const titleText = this.camelCasePipe.transform(titleData['title'] ?? '');
        const subTitleText = this.camelCasePipe.transform(titleData['subTitle'] ?? '');
        this.chartRef.setTitle({ text: titleText }, { text: subTitleText });
      } catch (e) {
        console.info('Chart setTitle error:', e); 
      }
    }
    return titleData
  }
  updateAxisTitles(e: any, config: any) {
    const yTitle = e?.seriesOptions?.yAxisTitle ?? config?.yAxis?.title;
    const xTitle = e?.seriesOptions?.xAxisTitle ?? config?.xAxis?.title
    if (yTitle) {
      e.target.yAxis[0].setTitle({ text: this.camelCasePipe.transform(yTitle ?? '') });
    }
    if (e.point && xTitle) {
      e.target.xAxis[0].setTitle({ text: this.camelCasePipe.transform(xTitle ?? '') });
    }
  };
  updateDrilldownTitles(e: any, config: any) {
    const titleSchema = config?.titleSchema;
    if (!titleSchema || !this.chartRef) { return };
    const titleData = this.updateTitle(true);
    if (!titleData) { return };
    const drilldownName = e.point?.name ?? '';
    const title = (titleData.title || '').replaceAll(DYNAMIC_IDENTIFIER['drilldownField'], drilldownName);
    const subTitle = (titleData.subTitle || '').replaceAll(DYNAMIC_IDENTIFIER['drilldownField'], drilldownName);
    try {
      this.chartRef.setTitle({ text: this.camelCasePipe.transform(title) }, { text: this.camelCasePipe.transform(subTitle) });
    } catch (err) {
      console.info('Chart drilldown setTitle error:', err); 
    }
  }
  formChartSchema(chartSchema: CommonChartModel) {
    const chartOptions: any = {
      chart: {
        height: '400px',
        ...(this.scrollablePlotAreaMinWidth > 0 && {
          scrollablePlotArea: {
            minWidth: this.scrollablePlotAreaMinWidth,
            opacity: 1
          }
        }),
        events: {
          drilldown: (e: any) => {
            const drillDownConfig = chartSchema?.dataSchema?.drillDownConfig;
            if (!drillDownConfig) { return }
            const chartTarget = e.target;
            this.drillAxisTitleStack.push(this.getCurrentAxisTitles(chartTarget));
            this.updateAxisTitles(e, drillDownConfig);
            this.updateDrilldownTitles(e, drillDownConfig);
            if (chartSchema?.type === CHART_TYPE['PieChart']) {
              const container = chartTarget?.renderTo;
              if (container) {
                container.classList.add('axisVisible');
                container.classList.remove('axisRemove');
              }
            }
          },
          drillup: (e: any) => {
            const chartTarget = e.target;
            const previousTitles = this.drillAxisTitleStack.pop();
            const nextXAxisTitle = previousTitles?.xTitle ?? chartSchema?.xAxis?.title?.text;
            const nextYAxisTitle = previousTitles?.yTitle ?? chartSchema?.yAxis?.title?.text;
            if (nextXAxisTitle) {
              chartTarget.xAxis[0].setTitle({ text: this.camelCasePipe.transform(nextXAxisTitle) });
            }
            if (nextYAxisTitle) {
              chartTarget.yAxis[0].setTitle({ text: this.camelCasePipe.transform(nextYAxisTitle) });
            }
            if (chartSchema?.type === CHART_TYPE['PieChart']) {
              chartSchema.xAxis = { visible: false };
              chartSchema.yAxis = { visible: false };
              const container = chartTarget?.renderTo;
              if (container) {
                container.classList.add('axisRemove');
                container.classList.remove('axisVisible');
              }
            }
            if (this.chartOptions.title) {
              this.updateTitle(false);
              this.chartOptions.title = { text: chartSchema?.title };
              this.chartOptions.subtitle = { text: chartSchema?.subtitle }
            }
          }
        }
      },
      title: {
        text: chartSchema?.title,
        align: 'center',
        style: {
          fontSize: '1rem',
          fontFamily: 'var(--cbl-font-family-nobel-bold)',
          letterSpacing: '.1rem',
        },
      },
      exporting: {
        enabled: false,
      },
      subtitle: {
        text: chartSchema?.subtitle ?? ''
      },
      series: chartSchema?.series,
      xAxis: chartSchema?.xAxis,
      yAxis: chartSchema?.yAxis,
      tooltip: (chartSchema?.tooltip) ? { ...chartSchema.tooltip } : { enabled: true }
    }
    if (!chartSchema.isLineChart) {
      chartOptions.chart.type = chartSchema.type;
    }
    if (this.chartType === CHART_TYPE['BarChart']) {
      if (chartOptions?.chart?.height) {
        chartOptions.chart.height = '600px'
      }
    }
    if (this.chartType === CHART_TYPE['ColumnChart'] || this.chartType === CHART_TYPE['BarChart']) {
      chartOptions['drilldown'] = chartSchema?.drilldown ?? { series: [] }
      chartOptions['plotOptions'] = chartSchema?.plotOptions ?? {
        series: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            align: 'center',
            verticalAlign: 'top',
            y: -25,
            style: {
              fontSize: '12px',
            }
          }
        }
      }
      chartOptions['legend'] = chartSchema?.legend ?? {
        enabled: false,
        reversed: true,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: -40,
        y: 80,
        floating: true,
        borderWidth: 1
      }
    } else if (this.chartType === CHART_TYPE['PieChart'] && chartSchema?.isDonut) {
      if (chartOptions?.chart?.height) {
        chartOptions.chart.height = '340px'
      }
      chartOptions['xAxis'] = { type: 'category' }
      chartOptions['drilldown'] = chartSchema?.drilldown ?? { series: [] }
      chartOptions['series'] = chartSchema?.series ? structuredClone(chartSchema.series) : undefined
      chartOptions['plotOptions'] = {
        series: { allowPointSelect: true },
        pie: {
          states: { inactive: { opacity: 1 } },
          dataLabels: chartSchema?.dataLabels ?? {
            enabled: true,
            format: `<b>{point.name}</b>: {point.y}`,
            style: { fontWeight: 'bold' },
          },
        },
      }
      if (chartSchema?.plotOptions?.['pie']) {
        chartOptions['plotOptions']['pie'] = {
          ...chartOptions['plotOptions']['pie'],
          ...chartSchema.plotOptions['pie'],
        };
      }
    } else if (this.chartType === CHART_TYPE['PieChart']) {
      if (chartOptions?.chart?.height) {
        chartOptions.chart.height = '340px'
      }
      chartOptions['xAxis'] = {
        type: 'category'
      }
      chartOptions['drilldown'] = chartSchema?.drilldown ?? { series: [] }
      chartOptions['plotOptions'] = {
        series: {
          allowPointSelect: true,
        },
        pie: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
          dataLabels: chartSchema?.dataLabels ?? {
            enabled: true,
            format: `<b>{point.name}</b>: {point.y}`,
            style: {
              fontWeight: 'bold',
            },
          },
        },
      }
    }
    return chartOptions;
  }
  fetchDataAndUpdateData(postParam?: any, isFieldSlctd = false) {
    this.isLoading = true;
    const requestId = ++this.latestFetchRequestId;
    if (this.apiConfig) {
      const fetchParams: SearchConfig = structuredClone(this.apiConfig)
      fetchParams['body'] ??= {};
      fetchParams['body'] = { ...fetchParams['body'], ...postParam };
      this.dataSource = [];
      this.chartRef = null;
      const urlPath = this.apiConfig['urlPath'] ?? '';
      this.apiErrorOccurred = false;
      this.isFieldSelected = isFieldSlctd;
      this.commonUtils.spinner('show', this.spinnerName);
      return this.commonService.getDataFromDataSource(fetchParams, urlPath).then(response => {
        if (requestId !== this.latestFetchRequestId) {
          return;
        }
        if (response['status'] !== API_STATUS['Success']) {
          response['result'] = [];
          this.apiErrorOccurred = true;
        }
        this.updateChartAfterFetch(response['result']);
        this.isLoading = false;
        this.commonUtils.spinner('hide', this.spinnerName);
      }).catch(err => {
        if (requestId !== this.latestFetchRequestId) {
          return;
        }
        console.info('Error :-', err)  
        this.isLoading = false;
        this.commonUtils.spinner('hide', this.spinnerName);
      });
    } else {
      this.isLoading = false;
      return Promise.resolve()
    }
  }

  private hasRequiredGlobalPayload(postParams: any): boolean {
    if (!this.requiredGlobalPayloadFields?.length) {
      return true;
    }

    return this.requiredGlobalPayloadFields.every((key) => {
      const value = postParams?.[key];
      return value !== '' && value !== null && value !== undefined;
    });
  }

  updatePayloadAndFetch(globalPayload?: any) {
    let postParams: any = {};
    if (globalPayload) {
      postParams = {
        ...globalPayload
      }
    }
    if (this.filterSchema?.FieldsArray) {
      this.filterSchema?.FieldsArray.forEach(filterField => {
        if (filterField['selectedValue'] && filterField['selectedValue'] !== null && filterField['selectedValue'] !== '') {
          if (filterField['payloadInfo']) {
            postParams = {
              ...postParams,
              ...filterField['payloadInfo']
            }
          }

          postParams[filterField['fieldName']] = filterField['selectedValue']
        }
      })
    }

    if (!this.hasRequiredGlobalPayload(postParams)) {
      this.dataSource = [];
      this.updateInfoFields();
      this.isLoading = !!this.defaultLoader;
      return;
    }

    this.fetchDataAndUpdateData(postParams)
  }
  async downloadActionOnClick() {
    const exportApiConfig = this.exportConfig?.downloadApiConfig;
    if (exportApiConfig?.path && exportApiConfig?.urlPath && exportApiConfig?.method) {
      const exportedFromApi = await this.commonService.exportUsingDownloadApi(exportApiConfig, this.filterSchema, this.exportConfig, this.spinnerName);
      if (exportedFromApi) {
        return;
      }
    }

    if (this.exportConfig?.groupedSheets && (this.exportConfig.groupedSheets.useResponseKeys || this.exportConfig.groupedSheets.mainSheetName || this.exportConfig.groupedSheets.dynamicSheets)) {
      this.exportNestedSheetsFromDataSource();
      return;
    }

    if (this.exportConfig) {
      const fileName = this.exportConfig.fileName ?? '';
      const description = this.exportConfig.descriptions ?? [];
      const drilldownName = this.chartSchema?.dataSchema?.drillDownConfig?.drillDownField;
      let result = this.dataSource;
      if (drilldownName) {
        result = result.flatMap(item => item[drilldownName] ?? []);
      }
      const data = this.commonUtils.formatExcelData(this.exportConfig, result, this.filterSchema)
      this.exportService.exportAsExcelFile(data, fileName, description)
    }
  }

  private exportNestedSheetsFromDataSource() {
    const exportConfiguration = this.exportConfig as exportConfig;
    const schemaMap = exportConfiguration.groupedSheets?.schemaMap ?? {};
    const rows = this.dataSource ?? [];
    const dataset: Record<string, any[]> = {};
    let mainSheetName: string | undefined;
    if (exportConfiguration.schema && rows.length > 0) {
      mainSheetName = exportConfiguration.groupedSheets?.mainSheetName ?? 'Summary';
      const mainSheetData = this.commonUtils.formatExcelData({ ...exportConfiguration, schema: exportConfiguration.schema }, rows, this.filterSchema);
      dataset[mainSheetName] = structuredClone(mainSheetData) ?? [];
    }

    if (exportConfiguration.groupedSheets?.dynamicSheets) {
      const sheetKeyField = exportConfiguration.groupedSheets.sheetKeyField;
      const sheetDataField = exportConfiguration.groupedSheets.sheetDataField;
      const sheetSchema = exportConfiguration.groupedSheets.sheetSchema;

      if (sheetKeyField && sheetDataField && sheetSchema) {
        rows.forEach(row => {
          const sheetName = row?.[sheetKeyField as string];
          const sheetData = row?.[sheetDataField as string];          
          if (sheetName && Array.isArray(sheetData) && sheetData.length > 0) {
            const formattedData = this.commonUtils.formatExcelData({ ...exportConfiguration, schema: sheetSchema }, sheetData, this.filterSchema);
            dataset[String(sheetName)] = structuredClone(formattedData) ?? [];
          }
        });
      }
    }
    Object.entries(schemaMap).forEach(([sourceKey, sheetConfig]: [string, any]) => {
      const sheetRows = rows.flatMap(row => Array.isArray(row?.[sourceKey]) ? row[sourceKey] : []);
      if (!sheetRows.length) { return; }
      const sheetName = sheetConfig?.sheetName ?? sourceKey;
      const schema = sheetConfig?.schema ?? sheetConfig;
      dataset[sheetName] = structuredClone(this.commonUtils.formatExcelData({ ...exportConfiguration, schema }, sheetRows, this.filterSchema)) ?? [];
    });
    const orderedEntries: Array<[string, any[]]> = [];
    const sheetOrder: string[] = [];    
    if (mainSheetName && dataset[mainSheetName]) {
      orderedEntries.push([mainSheetName, dataset[mainSheetName]]);
      sheetOrder.push(mainSheetName);
    }
    Object.entries(dataset).forEach(([key, value]) => {
      if (key !== mainSheetName) {
        orderedEntries.push([key, value]);
        sheetOrder.push(key);
      }
    });
    const orderedDatasetFinal = Object.fromEntries(orderedEntries);
    if (!Object.keys(orderedDatasetFinal).length) {
      this.commonUtils.showMatSnackBar('Export Failed - No Data Found');
      return;
    }
    this.exportService.exportToMultipleSheets(orderedDatasetFinal, exportConfiguration.fileName ?? '', undefined, sheetOrder);
  }
  async updateFilterFields() {
    if (this.defaultLoader && this.filterSchema?.globalPayloadSubject && !this.onLoadFetch) {
      this.isLoading = true;
    }

    if (this.filterSchema?.globalPayloadSubject) {
      this.globalpayloadSubject = this.filterSchema.globalPayloadSubject.subscribe(globalPayload => {
        if (this.globalFilterFields.includes(globalPayload['FieldName'])) {
          this.globalTitleFields = globalPayload?.Value?.title ?? {}
          if (globalPayload?.Value?.body) {
            this.globalpayload = structuredClone(globalPayload['Value']['body']);
            this.updateTitle(false)
            this.updatePayloadAndFetch(this.globalpayload)
          }
        }

      })
    }
    if (!this.filterSchema?.FieldsArray) {
      return;
    }
    await this.getOptions();
    this.filterSchema.onControlValueChanged = this.filterFieldOnChange;
    this.filterSchema.onButtonClicked = this.onButtonClicked;
    this.updateTitle(false);
  }
  async getOptions() {
    if (this.filterSchema?.FieldsArray) {
      this.filterSchema.FieldsArray.forEach((filterField: FormFieldConfig) => {
        if (FieldTypesForOptions.includes(filterField['fieldType'])) {
          if (filterField['options'] && filterField['options'].length > 0) {
            this.commonUtils.setDefaultValueForOptionsField(filterField);
            return Promise.resolve();
          }
          return Promise.resolve();
        } else {
          return Promise.resolve();
        }
      })
    }

  }
  filterFieldOnChange = (event: FieldSelectedValue) => {
    let selectedField: FormFieldConfig | undefined;
    let postParams = structuredClone(this.globalpayload ?? {});
    event?.FieldArray?.forEach(field => {
      if (field['isDependentOn'] === event['FieldName']) {
        field['selectedValue'] = '';
      } else if (field['fieldName'] === event['FieldName']) {
        selectedField = field;
        this.updateTitle(false, selectedField)
      }
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if (field['payloadInfo']) {
          postParams = {
            ...postParams,
            ...field['payloadInfo']
          }
        }
        postParams[field['fieldName']] = field['selectedValue']
      } else {
        delete postParams[field['fieldName']];
      }
    })

    if (event?.FieldArray?.length) {
      const hasNonEmptyField = event.FieldArray.some(field => field.selectedValue && field.selectedValue !== null && field.selectedValue !== '' && field.selectedValue.length > 0);
      const monthField = event.FieldArray.find(field => field.fieldName === FieldNames.Month && field.multiple && field.defaultValue && field.defaultValue.length > 0);
      if (!hasNonEmptyField && monthField) {
        monthField.isDefaultSet = true;
        monthField.selectedValue = Array.isArray(monthField.defaultValue) ? monthField.defaultValue : [monthField.defaultValue];
        if (monthField.payloadInfo) {
          postParams = {
            ...postParams,
            ...monthField.payloadInfo
          };
        }
        postParams[monthField.fieldName] = monthField.selectedValue;
      }
    }

    this.fetchDataAndUpdateData(postParams, true)
  }
  onButtonClicked = (_event: any) => {

  }
  updateChartAfterFetch(result: Array<any>) {
    const transformedResult = this.dataTransform ? this.dataTransform(result ?? []) : (result ?? []);
    this.chartRef = null;
    const shouldEnablePlotScroll = this.scrollablePlotAreaMinWidth > 0 &&
      transformedResult.length > this.scrollablePlotAreaRecordThreshold;
    if (this.chartOptions?.chart) {
      this.chartOptions.chart.scrollablePlotArea = shouldEnablePlotScroll
        ? { minWidth: this.scrollablePlotAreaMinWidth, opacity: 1 }
        : undefined;
      this.updateFlag = true;
    }
    this.handleResponse(transformedResult);
    this.dataSource = transformedResult;
    this.updateInfoFields()
  }

  private getValueByKey(data: any, key: string): any {
    if (data[key] !== undefined) {
      return data[key];
    }
    const lowercaseKey = key.toLowerCase();
    for (const dataKey of Object.keys(data)) {
      if (dataKey.toLowerCase() === lowercaseKey) {
        return data[dataKey];
      }
    }
    return undefined;
  }

  updateInfoFields() {
    if (this.infoFields) {
      this.infoFields.forEach(field => {
        if (field['infoType'] === INFO_TYPES['Total'] || field['infoType'] === INFO_TYPES['Count']) {
          if (Array.isArray(field['calculateBy'])) {
            field['value'] = this.dataSource?.reduce((sum, item) => {
              return sum + field['calculateBy'].reduce((innerSum: number, key: string | number) => {
                const value = this.getValueByKey(item, String(key));
                return innerSum + Number(value || 0);
              }, 0);
            }, 0);
          } else {
            field['value'] = this.dataSource?.reduce((sum, item) => {
              const value = this.getValueByKey(item, field['calculateBy']);
              return sum + Number(value || 0);
            }, 0);
          }

        }
      })
    }
  }
  formatChartData(dataSchema: ChartDataSchema, result: Array<any>) {
    const response: any = {
      data: [],
      drilldown: [],
      categories: []
    }
    if (result) {
      if (this.chartSchema['type'] === CHART_TYPE['BubbleChart']) {
        // For bubble charts, handle the data transformation
        response['data'] = result?.map((data: any, index: number) => {
          // If data is already in bubble format (has x, y, z)
          if (data.x !== undefined && data.y !== undefined && data.z !== undefined) {
            return {
              x: data.x,
              y: data.y,
              z: data.z,
              name: data.name || data[dataSchema['label']] || '',
              dataset: data
            };
          }
          // Transform from flat data
          return {
            x: index,
            y: Number(this.getValueByKey(data, dataSchema['value'])) || 0,
            z: (Number(this.getValueByKey(data, dataSchema['value'])) || 0) * 2,
            name: data[dataSchema['label']] || '',
            dataset: data
          };
        });
      } else if (this.chartSchema['type'] === CHART_TYPE['BarChart']) {
        if (Array.isArray(dataSchema['value'])) {
          const valueData: any = {};
          const valueKeys = Object.values(dataSchema['value']);
          result.forEach(data => {
            response['categories'].push(data[dataSchema['label']]);
            valueKeys.forEach(key => {
              valueData[key] ??= [];
              const value = this.getValueByKey(data, key);
              if (value === undefined || value === null) {
                valueData[key].push({ y: 0, dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: 'bar' })
              } else if (Number.isNaN(value)) {
                valueData[key].push({ y: value, dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: 'bar' })
              } else {
                valueData[key].push({ y: Number(value), dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: 'bar' })
              }

            })

          })
          response['data'] = valueData
        } else {
          response['data'] = {
            [dataSchema['value']]: result?.map(data => {
              const chartId = data[dataSchema['label']] ?? '';
              const value = this.getValueByKey(data, dataSchema['value']);
              let processedValue: number;
              if (value === undefined) {
                processedValue = 0;
              } else if (Number.isNaN(value)) {
                processedValue = value;
              } else {
                processedValue = Number(value);
              }
              const chartData: any = { name: chartId, y: processedValue, dataset: data };
              if (dataSchema['drillDownConfig'] && data[dataSchema['drillDownConfig']['drillDownField']]) {
                chartData['drilldown'] = chartId.toString().trim();
                response['drilldown'].push(...this.formatDrillDownData(dataSchema['drillDownConfig'], data[dataSchema['drillDownConfig']['drillDownField']], chartId.toString().trim(), data))
              }
              return chartData
            })
          }
        }
      } else if (this.chartSchema['type'] === CHART_TYPE['ColumnChart'] || this.chartSchema['isLineChart']) {
        if (Array.isArray(dataSchema['value'])) {
          const valueData: any = {};
          const valueKeys = Object.values(dataSchema['value']);
          result.forEach(data => {
            response['categories'].push(data[dataSchema['label']]);
            valueKeys.forEach(key => {
              valueData[key] ??= [];
              const value = this.getValueByKey(data, key);
              const chartType = this.chartSchema['isLineChart'] ? 'line' : 'column';
              if (value === undefined || value === null) {
                valueData[key].push({ y: 0, dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: chartType })
              } else if (Number.isNaN(value)) {
                valueData[key].push({ y: value, dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: chartType })
              } else {
                valueData[key].push({ y: Number(value), dataset: data, nameKey: dataSchema['tooltipLabelInfo'][key], type: chartType })
              }

            })

          })
          response['data'] = valueData
        }
        else {
          response['data'] = result?.map(data => {
            const chartId = data[dataSchema['label']] ?? '';
            const value = Number.isNaN(data[dataSchema['value']]) ? data[dataSchema['value']] : Number(data[dataSchema['value']])
            const chartData: any = { name: chartId.toString().trim(), y: value, dataset: data };
            if (dataSchema['drillDownConfig'] && data[dataSchema['drillDownConfig']['drillDownField']]) {
              chartData['drilldown'] = chartId.toString().trim();
              response['drilldown'].push(...this.formatDrillDownData(dataSchema['drillDownConfig'], data[dataSchema['drillDownConfig']['drillDownField']], chartId.toString().trim(), data))
            }
            return chartData
          })
        }
      } else if (this.chartSchema['type'] === CHART_TYPE['AreasplineChart']) {
        // For areaspline charts, just extract numeric values
        response['data'] = result?.map((data: any) => {
          const value = typeof data === 'number' ? data : this.getValueByKey(data, dataSchema['value']);
          const y = Number.isNaN(value) ? value : Number(value);
          if (typeof data === 'number') {
            return y;
          }
          const name = (data[dataSchema['label']] ?? '').toString().trim();
          return {
            ...data,
            name,
            y,
          };
        });
      } else {
        response['data'] = result?.map(data => {
          const chartId = data[dataSchema['label']] ?? '';
          const value = Number.isNaN(data[dataSchema['value']]) ? data[dataSchema['value']] : Number(data[dataSchema['value']])
          const chartData: any = { name: chartId.toString().trim(), y: value, dataset: data };
          if (dataSchema['drillDownConfig'] && data[dataSchema['drillDownConfig']['drillDownField']]) {
            chartData['drilldown'] = chartId.toString().trim();
            response['drilldown'].push(...this.formatDrillDownData(dataSchema['drillDownConfig'], data[dataSchema['drillDownConfig']['drillDownField']], chartId.toString().trim(), data))
          }
          return chartData
        })
      }
    }
    return response
  }

  private buildDrilldownSeries( drilldownConfig: DrillDownConfig,
    dataArr: Array<any>,
    seriesId: string,
    seriesName: string,
    primaryData: any,
    seriesCollector: Array<any>
  ) {
    const drilldownData: any = {
      name: seriesName,
      id: seriesId,
      type: drilldownConfig['type'],
      tooltip: drilldownConfig?.tooltip ?? { enabled: true },
      dataLabels: drilldownConfig?.dataLabels ?? {},
      xAxisTitle: drilldownConfig?.xAxis?.title,
      yAxisTitle: drilldownConfig?.yAxis?.title,
    }

    drilldownData['data'] = dataArr.map((data, _index) => {
      const point: any = {
        ...data,
        name: data[drilldownConfig['label']],
        y: data[drilldownConfig['value']],
        dataset: data,
        primaryData: primaryData,
      };

      const nestedConfig = drilldownConfig?.drillDownConfig;
      if (nestedConfig && data[nestedConfig['drillDownField']]) {
        const nestedId = `${seriesId}-${point.name}`.toString().trim();
        point.drilldown = nestedId;
        this.buildDrilldownSeries(nestedConfig, data[nestedConfig['drillDownField']], nestedId, String(point.name), data, seriesCollector);
      }

      return point;
    })

    seriesCollector.push(drilldownData);
  }

  formatDrillDownData(drilldownConfig: DrillDownConfig, dataArr: Array<any>, primaryId: string, primaryData: any) {
    const seriesCollector: Array<any> = [];
    this.buildDrilldownSeries(drilldownConfig, dataArr, primaryId, primaryId, primaryData, seriesCollector);
    return seriesCollector;
  }
  handleResponse(resultArr: Array<any>) {
    let chartResult: DataConfig = { data: [] }
    if (this.chartSchema['dataSchema']) {
      const dataSchema = this.chartSchema['dataSchema'];
      chartResult = this.formatChartData(dataSchema, resultArr);
      this.formattedResult = chartResult['data']
    } else {
      this.commonUtils.showMatSnackBar('Error formatting chart data dataSchema missing')
    }
    this.updateChartBasedOnType(chartResult)
    return chartResult
  }
  updateChartBasedOnType(data: DataConfig) {
    if (this.chartSchema) {
      if (this.chartSchema['type'] === CHART_TYPE['ColumnChart'] || this.chartSchema['isLineChart']) {
        this.updateColumnChartData(data['data'], data['categories'], data['drilldown']);
      }
      else if (this.chartSchema['type'] === CHART_TYPE['PieChart']) {
        this.updateChartData(data['data'], data['drilldown']);
      }
      else if (this.chartSchema['type'] === CHART_TYPE['BarChart']) {
        this.updateBarChartData(data['data'], data['categories'] || [], data['drilldown']);
      }
      else if (this.chartSchema['type'] === CHART_TYPE['BubbleChart']) {
        this.updateBubbleChartData(data['data']);
      }
      else if (this.chartSchema['type'] === CHART_TYPE['AreasplineChart']) {
        this.updateAreasplineChartData(data['data']);
      }
    }
  }
  updateChartData(data: Array<any>, drilldown?: Array<any>) {
    if (this.chartSchema['series']?.length === 1) {
      this.chartSchema['series'][0]['data'] = data;
      if (this.chartOptions?.series?.length === 1) {
        this.chartOptions.series[0].data = data;
        this.updateFlag = true;
      }
    }
    if (this.chartSchema['drilldown'] && drilldown) {
      this.chartSchema['drilldown']['series'] = drilldown;
    }
  }
  updateColumnChartData(data: any, categories?: Array<any>, drilldown?: Array<any>) {
    if (this.chartSchema['series']?.length === 1) {
      this.chartSchema['series'][0]['data'] = data;
    }
    else if (this.chartSchema['series'] && this.chartSchema['series'].length > 1) {
      this.chartSchema['series'].forEach((series) => {
        const key = series['key'];
        if (key) {
          if (data[key]) {
            series['data'] = data[key];
          } else {
            const dataKey = Object.keys(data).find(k => k.toLowerCase() === key.toLowerCase());
            if (dataKey) {
              series['data'] = data[dataKey];
            }
          }
        }
      })
      if (this.chartSchema['xAxis']) {
        this.chartSchema['xAxis']['categories'] = categories;
      }
    }
    if (this.chartSchema['drilldown'] && drilldown) {
      this.chartSchema['drilldown']['series'] = drilldown;
    }
  }
  updateBarChartData(data: any, categories: Array<any>, drilldown?: Array<any>) {
    if (this.chartSchema['series']?.length === 1) {
      data = Object.values(data)[0];
      this.chartSchema['series'][0]['data'] = data;
      if (this.chartSchema['xAxis']) {
        this.chartSchema['xAxis']['categories'] = data.map((x: { name: any; }) => x.name);
      }
    } else if (this.chartSchema['series'] && this.chartSchema['series'].length > 1) {
      this.chartSchema['series'].forEach((series) => {
        series['data'] = data[series['key']]
      })
      if (this.chartSchema['xAxis']) {
        this.chartSchema['xAxis']['categories'] = categories;
      }
    }
    if (this.chartSchema['drilldown'] && drilldown) {
      this.chartSchema['drilldown']['series'] = drilldown;
    }
  }

  updateBubbleChartData(data: any) {
    if ( this.chartSchema?.['series'] && this.chartSchema['series']?.length >= 1) {
      if (Array.isArray(data) && data.length > 0 && data[0].x !== undefined) {
        this.chartSchema['series'][0]['data'] = data;
      } else {
        const bubbleData = Object.values(data)[0];
        if (Array.isArray(bubbleData)) {
          this.chartSchema['series'][0]['data'] = bubbleData;
        }
      }
    }
  }

  updateAreasplineChartData(data: any) {
    if (this.chartSchema?.['series'] && this.chartSchema['series']?.length >= 1) {
      if (Array.isArray(data)) {
        this.chartSchema['series'][0]['data'] = data;
      } else {
        const areaData = Object.values(data)[0];
        if (Array.isArray(areaData)) {
          this.chartSchema['series'][0]['data'] = areaData;
        }
      }
    }
  }
}

